import { BookmarksState } from './bookmarks/types';
import { Store } from '@reduxjs/toolkit';
import { ReducersInjectorWithState, StoreType } from '@wajeez-web/stores/base';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { AuthorsState } from './authors/types';
import authorsReducer from './authors/slices';
import { NarratorsState } from './narrators/types';
import narratoreReducer from './narrators/slices';
import { ContentEntityTypeState } from './contentEntityTypes/types';
import contentEntityTypesReducer from './contentEntityTypes/slices';
import { DailyContentState } from './dailyContent/types';
import dailyContentReducer from './dailyContent/slices';
import { SuggestContentState } from './suggestContent/types';
import suggestContentReducer from './suggestContent/slices';
import { QuotesState } from './quotes/types';
import quotesReducer from './quotes/slices';
import { ContentEntityState } from './contentEntities/types';
import contentEntitiesReducer from './contentEntities/slices';
import { ContentEntityaudiosState } from './contentEntityAudios/types';
import contentEntityAudiosReducer from './contentEntityAudios/slices';
import { AnalysisState } from './analysis/types';
import analysisReducer from './analysis/slices';
import { FlowsState } from './flows/types';
import flowsReducer from './flows/slices';
import { FlowSelectionsState } from './flowSelections/types';
import flowSelectionsReducer from './flowSelections/slices';
import { CollectionComponentTypeState } from './collectionComponentTypes/types';
import collectionComponentTypesReducer from './collectionComponentTypes/slices';
import { CollectionComponentsState } from './collectionComponents/types';
import collectionComponentsReducer from './collectionComponents/slices';
import { ContentEntityTextState } from './contentEntityTexts/types';
import contentEntityTextsReducer from './contentEntityTexts/slices';
import componentsReducer from './components/slices';
import { ComponentsState } from './components/types';
import { SessionsState } from './sessions/types';
import sessionsReducer from './sessions/slices';
import { InterestsState } from './interests/types';
import interestsReducer from './interests/slices';
import bookmarksReducer from './bookmarks/slices';
import { HighlightsState } from './highlights/types';
import highlightsReducer from './highlights/slices';
import { RatingState } from './rating/types';
import ratingReducer from './rating/slices';
import { FollowingsState } from './followings/types';
import followingsReducer from './followings/slices';
import { MyLibraryState } from './myLibrary/types';
import myLibraryReducer from './myLibrary/slices';
import { QuizzesState } from './quizzes/types';
import quizzesReducer from './quizzes/slices';
import { QuestionsState } from './questions/types';
import questionsReducer from './questions/slices';
import { AnswersState } from './answers/types';
import answersReducer from './answers/slices';
import { ComponentTypesState } from './componentTypes/types';
import componentTypesReducer from './componentTypes/slices';
import { TagsState } from './tags/types';
import tagsReducer from './tags/slices';
import { SystemConfigsState } from './systemConfigs/types';
import systemConfigsReducer from './systemConfigs/slices';
import { ContentEntityTypeNavigationsState } from './contentEntityTypeNavigations/types';
import contentEntityTypeNavigationsReducer from './contentEntityTypeNavigations/slices';
import { ContentEntityListsState } from './contentEntityLists/types';
import contentEntityListsReducer from './contentEntityLists/slices';
import { LanguagesState } from './languages/types';
import languagesReducer from './languages/slices';
import { ContentEntityStructureState } from './contentEntityStructures/types';
import contentEntityStructuresReducer from './contentEntityStructures/slices';
import { BadgeState } from './badges/types';
import badgesReducer from './badges/slices';
import { AllowingsState } from './allowings';
import allowingsReducer from './allowings/slices';
import { PublisherState } from './publishers/types';
import publishersReducer from './publishers/slices';
import voiceActorsReducer from './voiceActors/slices';
import { VoiceActorState } from './voiceActors/types';

export let contentStore: StoreType<{
  authors: AuthorsState;
  contentEntityType: ContentEntityTypeState;
  narrators: NarratorsState;
  dailyContent: DailyContentState;
  suggestContent: SuggestContentState;
  contentEntity: ContentEntityState;
  contentEntityaudios: ContentEntityaudiosState;
  contentEntityTexts: ContentEntityTextState;
  quotes: QuotesState;
  analysis: AnalysisState;
  flows: FlowsState;
  flowSelections: FlowSelectionsState;
  collectionComponentTypes: CollectionComponentTypeState;
  collectionComponents: CollectionComponentsState;
  components: ComponentsState;
  componentTypes: ComponentTypesState;
  sessions: SessionsState;
  interests: InterestsState;
  bookmarks: BookmarksState;
  highlights: HighlightsState;
  rating: RatingState;
  followings: FollowingsState;
  myLibrary: MyLibraryState;
  quizzes: QuizzesState;
  questions: QuestionsState;
  answers: AnswersState;
  tags: TagsState;
  systemConfigs: SystemConfigsState;
  contentEntityTypeNavigations: ContentEntityTypeNavigationsState;
  contentEntityLists: ContentEntityListsState;
  languages: LanguagesState;
  contentEntityStructures: ContentEntityStructureState;
  badges: BadgeState;
  allowings: AllowingsState;
  publishers: PublisherState;
  voiceActors: VoiceActorState;
}>;

// export type StoreType = typeof store;
export type ContentDispatch = typeof contentStore.dispatch;
export type ContentStoreState = ReturnType<typeof contentStore.getState>;
export const useContentDispatch = () => useDispatch<ContentDispatch>();
export const useContentSelector: TypedUseSelectorHook<ContentStoreState> =
  useSelector;
export const getContentState = (store: Store) =>
  store.getState() as ContentStoreState;

export const injectContentReducers: ReducersInjectorWithState<
  ContentStoreState
> = () => {
  return [
    {
      name: 'authors',
      reducer: authorsReducer,
    },
    {
      name: 'contentEntityType',
      reducer: contentEntityTypesReducer,
    },
    {
      name: 'narrators',
      reducer: narratoreReducer,
    },
    {
      name: 'dailyContent',
      reducer: dailyContentReducer,
    },
    {
      name: 'suggestContent',
      reducer: suggestContentReducer,
    },
    {
      name: 'contentEntity',
      reducer: contentEntitiesReducer,
    },
    {
      name: 'contentEntityaudios',
      reducer: contentEntityAudiosReducer,
    },
    {
      name: 'contentEntityTexts',
      reducer: contentEntityTextsReducer,
    },
    {
      name: 'quotes',
      reducer: quotesReducer,
    },
    {
      name: 'analysis',
      reducer: analysisReducer,
    },
    {
      name: 'flows',
      reducer: flowsReducer,
    },
    {
      name: 'flowSelections',
      reducer: flowSelectionsReducer,
    },
    {
      name: 'collectionComponentTypes',
      reducer: collectionComponentTypesReducer,
    },
    {
      name: 'collectionComponents',
      reducer: collectionComponentsReducer,
    },
    {
      name: 'components',
      reducer: componentsReducer,
    },
    {
      name: 'componentTypes',
      reducer: componentTypesReducer,
    },
    {
      name: 'sessions',
      reducer: sessionsReducer,
    },
    {
      name: 'interests',
      reducer: interestsReducer,
    },
    {
      name: 'highlights',
      reducer: highlightsReducer,
    },
    { name: 'bookmarks', reducer: bookmarksReducer },
    { name: 'rating', reducer: ratingReducer },
    { name: 'followings', reducer: followingsReducer },
    { name: 'myLibrary', reducer: myLibraryReducer },
    {
      name: 'quizzes',
      reducer: quizzesReducer,
    },
    {
      name: 'questions',
      reducer: questionsReducer,
    },
    {
      name: 'answers',
      reducer: answersReducer,
    },
    {
      name: 'tags',
      reducer: tagsReducer,
    },
    {
      name: 'systemConfigs',
      reducer: systemConfigsReducer,
    },
    {
      name: 'contentEntityTypeNavigations',
      reducer: contentEntityTypeNavigationsReducer,
    },
    {
      name: 'contentEntityLists',
      reducer: contentEntityListsReducer,
    },
    {
      name: 'languages',
      reducer: languagesReducer,
    },
    {
      name: 'contentEntityStructures',
      reducer: contentEntityStructuresReducer,
    },
    {
      name: 'badges',
      reducer: badgesReducer,
    },
    {
      name: 'allowings',
      reducer: allowingsReducer,
    },
    {
      name: 'publishers',
      reducer: publishersReducer,
    },
    {
      name: 'voiceActors',
      reducer: voiceActorsReducer,
    },
  ];
};
